'use client';

import { ResponsiveContext } from '@/components/Responsive/ResponsiveProvider';
import { useStore } from '@/providers/app-store-provider';
import appCommunicationManager from '@/utils/app-communication-manager';
import logger from '@/utils/client-logger';
import { EAppCommunicationFuncName } from '@/utils/enums/app-sdk';
import { EPlatform } from '@/utils/enums/platform-enum';
import { useContext, useEffect } from 'react';
import { useTransitionTracker } from '../hooks/useTransitionTracker';
import FaqPart from './FaqPart';
import LensDescription from './LensDescription';
import LensPart from './LensPart';
import SeriesOverview from './SeriesOverview';

// 类型定义
interface TransitionLensesData {
  series_overview?: any;
  Lens_description?: any;
  Lens_series1?: any;
  Lens_series2?: any;
  Lens_series3?: any;
  faq_part?: any;
  statement?: string;
  seo_info?: any;
  limit?: number;
}

interface Props {
  transitionData: TransitionLensesData;
}

export default function TransitionLensesContent({ transitionData }: Props) {
  const { trackPageView } = useTransitionTracker();

  // App相关状态
  const { deviceType } = useContext(ResponsiveContext);
  const inAppH5 = [EPlatform.ANDROID, EPlatform.IOS].includes(deviceType);
  // const token = useStore((state) => state.token);
  const setToken = useStore((state) => state.setToken);
  const setUserInfo = useStore((state) => state.setUserInfo);
  const refreshAccountInfo = useStore((state) => state.refreshAccountInfo);

  // 页面加载埋点
  useEffect(() => {
    trackPageView();
  }, [trackPageView]);

  // 在App内嵌环境时初始化通信
  useEffect(() => {
    if (inAppH5) {
      handleAppCommunication();
      window.addEventListener('appView', trackPageView);
      return () => {
        window.removeEventListener('appView', trackPageView);
      };
    }
  }, [inAppH5]);

  // 处理App通信逻辑
  const handleAppCommunication = async () => {
    try {
      // 注册token回调通信
      appCommunicationManager.registerCallback(
        EAppCommunicationFuncName.APP_LOGIN_SUCCESS,
        async (_token) => {
          logger.log('App登录token:', _token);
          if (_token !== 'ClearToken') {
            setToken(_token);
            // 获取用户信息
            await refreshAccountInfo();
          } else {
            // 清空当前用户信息
            setToken('');
            setUserInfo({});
          }
        },
        {},
        'BackTokenInfo' as any, // 使用BackTokenInfo的postMessage方法
      );
    } catch (error) {
      logger.error('App通信初始化失败:', error);
    }
  };

  // 组件销毁时清理App通信管理器
  useEffect(() => {
    return () => {
      if (inAppH5) {
        appCommunicationManager.cleanup();
      }
    };
  }, [inAppH5]);

  return (
    <>
      {/* 系列概览区域 */}
      <SeriesOverview seriesOverviewData={transitionData.series_overview} />

      {/* 镜片说明区域 */}
      <LensDescription lensDescriptionData={transitionData.Lens_description} />

      {/* 镜片系列1 */}
      <LensPart lensPartData={transitionData.Lens_series1} />

      {/* 镜片系列2 */}
      <LensPart lensPartData={transitionData.Lens_series2} />

      {/* 镜片系列3 */}
      <LensPart lensPartData={transitionData.Lens_series3} />

      {/* FAQ和声明部分 */}
      <FaqPart faqData={transitionData.faq_part} statement={transitionData.statement} />
    </>
  );
}
